/* Jitsi Meet app main entrypoint. */

// Re-export jQuery
// FIXME: Remove this requirement from torture tests.
import $ from 'jquery';

window.$ = window.jQuery = $;

import '@matrix-org/olm';

import 'focus-visible';

/*
* Safari polyfill for createImageBitmap
* https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/createImageBitmap
*
* Support source image types: Canvas.
*/
if (!('createImageBitmap' in window)) {
    window.createImageBitmap = function(data) {
        return new Promise((resolve, reject) => {
            let dataURL;

            if (data instanceof HTMLCanvasElement) {
                dataURL = data.toDataURL();
            } else {
                reject(new Error('createImageBitmap does not handle the provided image source type'));
            }
            const img = document.createElement('img');

            img.addEventListener('load', () => {
                resolve(img);
            });
            img.src = dataURL;
        });
    };
}

// We need to setup the jitsi-local-storage as early as possible so that we can start using it.
// NOTE: If jitsi-local-storage is used before the initial setup is performed this will break the use case when we use
// the  local storage from the parent page when the localStorage is disabled. Also the setup is relying that
// window.location is not changed and still has all URL parameters.
import './react/features/base/jitsi-local-storage/setup';
import conference from './conference';
import API from './modules/API';
import UI from './modules/UI/UI';
import translation from './modules/translation/translation';
import { appNavigate } from './react/features/app/actions';
import { updateSettings } from './react/features/base/settings/actions';
import { setJWT } from './react/features/base/jwt/actions';

const COURSIO_RETURN_URL_KEY = 'coursio.returnURL';
const COURSIO_MESSAGE_TYPE = 'coursio:join';

/**
 * Persist the page URL the user came from before landing on a meeting, so we
 * can return there after the call ends.
 */
((win) => {
    const { pathname, href } = win.location;
    const referrer = win.document.referrer;

    if (!referrer
        || pathname === '/'
        || pathname.startsWith('/static/')) {
        return;
    }

    try {
        const refUrl = new URL(referrer, href);

        if (refUrl.href !== href) {
            win.sessionStorage.setItem(COURSIO_RETURN_URL_KEY, refUrl.href);
        }
    } catch (error) {
        // Ignore invalid referrer values (about:blank, etc).
    }
})(window);

// Initialize Olm as early as possible.
if (window.Olm) {
    window.Olm.init().catch(e => {
        console.error('Failed to initialize Olm, E2EE will be disabled', e);
        delete window.Olm;
    });
}

window.APP = {
    API,
    conference,
    translation,
    UI
};

const coursioMessageQueue = [];
let appStoreReference;

/**
 * Attempts to dispatch Coursio-specific navigation and user updates. Returns
 * {@code true} if the payload has been handled immediately, {@code false}
 * otherwise (in which case it should be queued until the Redux store exists).
 *
 * @param {Object} payload - The message payload to handle.
 * @returns {boolean}
 */
function dispatchCoursioPayload(payload = {}) {
    const store = appStoreReference || window.APP?.store;

    if (!store) {
        return false;
    }

    const { dispatch } = store;
    const { roomName, user, jwt } = payload;

    // Set JWT token if provided (for authentication)
    if (jwt) {
        dispatch(setJWT(jwt));
    }

    if (roomName) {
        window.sessionStorage.setItem('coursio.lastRoom', roomName);
        dispatch(appNavigate(roomName));
    }

    if (user) {
        const { displayName, email, avatarURL } = user;
        const settings = {};

        if (displayName) {
            settings.displayName = displayName;
        }

        if (email) {
            settings.email = email;
        }

        if (avatarURL) {
            settings.avatarURL = avatarURL;
        }

        if (Object.keys(settings).length) {
            dispatch(updateSettings(settings));
        }
    }

    return true;
}

/**
 * Flushes any queued Coursio messages now that the Redux store is ready.
 *
 */
function flushCoursioQueue() {
    while (coursioMessageQueue.length) {
        const payload = coursioMessageQueue[0];

        if (dispatchCoursioPayload(payload)) {
            coursioMessageQueue.shift();
        } else {
            break;
        }
    }
}

try {
    Object.defineProperty(window.APP, 'store', {
        configurable: true,
        enumerable: true,
        get() {
            return appStoreReference;
        },
        set(store) {
            appStoreReference = store;
            flushCoursioQueue();
        }
    });
} catch (error) {
    console.warn('Failed to augment APP.store with Coursio hooks.', error);
}

window.addEventListener('message', event => {
    let data = event.data;

    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch (_error) {
            return;
        }
    }

    if (!data || typeof data !== 'object' || data.type !== COURSIO_MESSAGE_TYPE) {
        return;
    }

    const payload = {
        roomName: data.roomName,
        user: data.user,
        jwt: data.jwt
    };

    if (!dispatchCoursioPayload(payload)) {
        coursioMessageQueue.push(payload);
    }
});

// TODO The execution of the mobile app starts from react/index.native.js.
// Similarly, the execution of the Web app should start from react/index.web.js
// for the sake of consistency and ease of understanding. Temporarily though
// because we are at the beginning of introducing React into the Web app, allow
// the execution of the Web app to start from app.js in order to reduce the
// complexity of the beginning step.
import './react';
